// src/components/common/Header.jsx
import React, { useState, useEffect } from 'react';
import {
    AppBar, Toolbar, Typography, Button, IconButton, Box,
    Menu, MenuItem, Container, useMediaQuery, useTheme,
    Drawer, List, ListItem, ListItemButton, ListItemIcon,
    ListItemText, Divider, Chip,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    Home as HomeIcon,
    Analytics as AnalyticsIcon,
    Science as ScienceIcon,
    Compare as CompareIcon,
    MenuBook as DocsIcon,
    Favorite as SponsorIcon,
    AccountCircle as AccountIcon,
    CalendarMonth as CalendarIcon,
    Code as CodeIcon,
    KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import MobileNavigation, { MobileDrawerContent } from './MobileNavigation';
import theme, { designTokens } from '../../styles/theme';

const navItems = [
    { label: 'Home',          path: '/',              icon: <HomeIcon /> },
    { label: 'Analysis',      path: '/analysis',      icon: <AnalyticsIcon /> },
    { label: 'Research',      path: '/research',      icon: <ScienceIcon /> },
    { label: 'Comparison',    path: '/comparison',    icon: <CompareIcon /> },
    { label: 'Documentation', path: '/documentation', icon: <DocsIcon /> },
    { label: 'Sponsorship',   path: '/sponsorship',   icon: <SponsorIcon /> },
];

const audienceConfig = {
    researcher: { label: 'Researcher', emoji: '🔬' },
    student:    { label: 'Student',    emoji: '📚' },
    worker:     { label: 'Worker',     emoji: '👷' },
    investor:   { label: 'Investor',   emoji: '💼' },
};

const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl]     = useState(null);
    const [scrolled, setScrolled]     = useState(false);
    const { state, dispatch } = useAppContext();
    const navigate  = useNavigate();
    const location  = useLocation();
    const theme     = useTheme();
    const isMobile  = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const handleAudienceChange = (audience) => {
        dispatch({ type: 'SET_AUDIENCE', payload: audience });
        setAnchorEl(null);
        navigate('/');
    };

    const isActive = (path) =>
        path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

    const drawer = (
        <MobileDrawerContent onClose={() => setMobileOpen(false)} />
    );

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    bgcolor: scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.98)',
                    backdropFilter: scrolled ? 'blur(12px)' : 'none',
                    borderBottom: scrolled ? `1px solid ${designTokens.overlays.dark}` : '1px solid transparent',
                    boxShadow: scrolled ? '0 4px 24px -4px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.3s ease',
                    color: 'text.primary',
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ gap: 1, minHeight: { xs: 60, md: 68 } }}>
                        {isMobile && (
                            <IconButton
                                edge="start"
                                onClick={() => setMobileOpen(true)}
                                sx={{ color: 'text.primary', mr: 0.5 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}

                        {/* Logo */}
                        <Box
                            onClick={() => navigate('/')}
                            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1.5, flexShrink: 0 }}
                        >
                            <Box
                                sx={{
                                    width: 38, height: 38, borderRadius: 2,
                                    background: theme.palette.gradients.primary,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: designTokens.shadows.glow.primary,
                                }}
                            >
                                <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1rem', lineHeight: 1 }}>
                                    AP
                                </Typography>
                            </Box>
                            <Typography
                                noWrap
                                sx={{
                                    fontWeight: 800,
                                    fontSize: '1.05rem',
                                    background: theme.palette.gradients.primary,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    display: { xs: 'none', sm: 'block' },
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                AutoPostureCV
                            </Typography>
                        </Box>

                        {/* Desktop nav */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', gap: 0.25, ml: 2.5, flex: 1 }}>
                                {navItems.map((item) => {
                                    const active = isActive(item.path);
                                    return (
                                        <Button
                                            key={item.label}
                                            onClick={() => navigate(item.path)}
                                            sx={{
                                                color: active ? 'primary.main' : 'text.secondary',
                                                fontWeight: active ? 700 : 500,
                                                fontSize: '0.82rem',
                                                px: 1.25,
                                                py: 0.75,
                                                borderRadius: 2,
                                                position: 'relative',
                                                bgcolor: active ? designTokens.overlays.primary : 'transparent',
                                                '&:hover': {
                                                    bgcolor: designTokens.overlays.primary,
                                                    color: 'primary.main',
                                                    transform: 'none',
                                                },
                                                '&::after': active ? {
                                                    content: '""',
                                                    position: 'absolute',
                                                    bottom: 4,
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: 16,
                                                    height: 2,
                                                    borderRadius: 1,
                                                    bgcolor: 'primary.main',
                                                } : {},
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Audience switcher - moved to left side */}
                        {!isMobile && (
                            <Button
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                endIcon={<ArrowDownIcon />}
                                startIcon={
                                    <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>
                                        {audienceConfig[state.audience].emoji}
                                    </Typography>
                                }
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.82rem',
                                    fontWeight: 500,
                                    bgcolor: designTokens.overlays.dark,
                                    borderRadius: 2,
                                    px: 1.25,
                                    py: 0.75,
                                    ml: 1,
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: designTokens.overlays.darker, transform: 'none' },
                                }}
                            >
                                {audienceConfig[state.audience].label}
                            </Button>
                        )}

                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
                            PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 180, boxShadow: '0 8px 30px rgba(0,0,0,0.12)' } }}
                        >
                            {Object.entries(audienceConfig).map(([key, val]) => (
                                <MenuItem
                                    key={key}
                                    onClick={() => handleAudienceChange(key)}
                                    selected={state.audience === key}
                                    sx={{ borderRadius: 1, mx: 0.5, my: 0.25, fontWeight: state.audience === key ? 700 : 400 }}
                                >
                                    {val.emoji}&nbsp;&nbsp;{val.label} View
                                </MenuItem>
                            ))}
                        </Menu>

                        <Box sx={{ flexGrow: 1 }} />

                        {/* Book Demo CTA pill */}
                        <Button
                            variant="ctaGradient"
                            size="small"
                            startIcon={<CalendarIcon sx={{ fontSize: '1rem !important' }} />}
                            onClick={() => navigate('/book-demo')}
                            sx={{
                                borderRadius: 3,
                                px: { md: 1.75, lg: 2.25 },
                                py: 0.8,
                                fontSize: '0.82rem',
                                whiteSpace: 'nowrap',
                                display: { xs: 'none', sm: 'flex' },
                            }}
                        >
                            Book Demo
                        </Button>

                        {/* Technical Consultation button - moved after Book Demo */}
                        {!isMobile && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<CodeIcon sx={{ fontSize: '1rem !important' }} />}
                                onClick={() => navigate('/technical-consultation')}
                                sx={{
                                    background: theme.palette.gradients.secondary,
                                    borderRadius: 3,
                                    px: { md: 1.75, lg: 2.25 },
                                    py: 0.8,
                                    ml: 0.75,
                                    fontSize: '0.82rem',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    boxShadow: designTokens.shadows.glow.purple,
                                    '&:hover': {
                                        boxShadow: `0 4px 20px rgba(124,58,237,0.45)`,
                                        background: theme.palette.gradients.secondary,
                                    },
                                    display: { xs: 'none', sm: 'flex' },
                                }}
                            >
                                Consult
                            </Button>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { width: 300, borderRadius: '0 16px 16px 0' },
                }}
            >
                {drawer}
            </Drawer>

            {/* Mobile Bottom Navigation */}
            <MobileNavigation onMenuToggle={() => setMobileOpen(true)} />
        </>
    );
};

export default Header;
